/**
 * Team-hierarchy reads over the materialized `path`.
 *
 * One home for the subtree rule, because it had grown two client-side copies —
 * `teamFamilyIds` in `stores/team-sla.store.ts` and the parent picker's own —
 * each with its own docblock claiming to mirror the server. A rule stated twice
 * is a rule that drifts, and only one of the two copies had the regression test.
 *
 * The type-only `CommandSelectOption` import is erased at build time, so this
 * stays a pure module with no runtime dependency on the UI layer.
 */
import type { CommandSelectOption } from "@/components/ui/command-select";
import type { TTeam } from "@/types/team";

/** Is *candidate* the team itself, or somewhere in its subtree?
 *
 *  `path` is the materialized ancestor chain (`/root-id/child-id/…`, one segment
 *  per level), so a strict descendant is exactly a row whose path starts with
 *  this team's path plus a separator.
 *
 *  **The trailing `/` is load-bearing, and this predicate must stay identical to
 *  the three that encode it server-side**: `expand_team_ids_with_descendants`
 *  (`teams/team_tree.py`), the `path LIKE OLD.path || '/%'` subtree cascade in
 *  `db/migrations/0288_team_reparent_path_triggers.py`, and the cycle guard in
 *  `TeamDetailEndpoint.patch`. Drop the separator and a bare `startsWith(path)`
 *  matches any sibling whose id merely begins with the same characters — greying
 *  out teams that are perfectly legal parents.
 *
 *  Callers rely on this being decidable from a loaded `TTeam` alone: `path` is
 *  served on every row, so no consumer needs a descendants request.
 */
export function isSelfOrDescendant(candidate: TTeam, team: TTeam): boolean {
  return candidate.id === team.id || candidate.path.startsWith(`${team.path}/`);
}

/** Does *team* have any sub-teams beneath it in *teams*?
 *
 *  Asked as "are there descendants" rather than "are there direct children"
 *  because the two are equivalent — a descendant at any depth implies a direct
 *  child — and this way the one predicate answers both.
 *
 *  **Only as complete as the list handed in.** `visible_team_ids` expands DOWN
 *  only, so a viewer always sees their own team's subtree; the answer is
 *  therefore reliable for the parent picker's audience (an admin sees every
 *  team, and a lead sees their own descendants). A caller passing a narrower
 *  list gets a narrower answer.
 */
export function hasSubTeams(teams: TTeam[], team: TTeam): boolean {
  return teams.some((candidate) => candidate.id !== team.id && isSelfOrDescendant(candidate, team));
}

/** *team* plus every team beneath it, as ids. `null` when *teamId* is not in *teams*.
 *
 *  Narrowing is the safe direction for the SLA-scope caller, which logs the
 *  `null` — the server still decides.
 */
export function teamFamilyIds(teams: TTeam[], teamId: string): Set<string> | null {
  const team = teams.find((t) => t.id === teamId);
  if (!team) return null;
  return new Set(teams.filter((c) => isSelfOrDescendant(c, team)).map((c) => c.id));
}

/** The rows a team's Parent-team picker offers, in name order (NG-1147).
 *
 *  Pure and separate from the field because it carries two invariants worth
 *  asserting without rendering:
 *
 *  1. **No option is a cycle.** The team itself and its descendants are present
 *     but `disabled`, so the rule is visible rather than discovered when the save
 *     is refused. Each carries a reason — a locked row with none reads as a bug.
 *  2. **The current parent is always in the list.** `teams` holds only the teams
 *     the viewer can read, and `visible_team_ids` expands DOWN only, so a lead of
 *     a child team never receives its parent's row. `CommandSelect` drives its
 *     trigger off *resolvable* selections, so an absent `value` renders as the
 *     placeholder — the field would claim the team is root when it is not.
 *     `TeamSerializer.parent_name` is served for exactly this (NG-1146); this is
 *     the read that consumes it, and appending the current value when the option
 *     list lacks it is the same thing `event-parent-select` and
 *     `work-item-relations` already do.
 *
 *  Invariant 2 is conditional on `parent_name`, which `TeamSerializer` sources
 *  from `parent.name` — so it is non-null exactly when `parent` is, and the
 *  branch cannot be skipped by a row the API can actually produce.
 */
export function parentTeamOptions(
  teams: TTeam[],
  team: TTeam,
  notes: { self: string; descendant: string },
): CommandSelectOption[] {
  const options: CommandSelectOption[] = teams.map((candidate) =>
    isSelfOrDescendant(candidate, team)
      ? {
          id: candidate.id,
          name: candidate.name,
          disabled: true,
          // A note on the row rather than a tooltip: it needs no hover and covers
          // no other option — see `CommandSelectOption.disabledNote`.
          disabledNote: candidate.id === team.id ? notes.self : notes.descendant,
        }
      : { id: candidate.id, name: candidate.name },
  );

  if (!team.parent || !team.parent_name || options.some((o) => o.id === team.parent)) {
    return options;
  }
  return [...options, { id: team.parent, name: team.parent_name }].sort((a, b) =>
    a.name.localeCompare(b.name),
  );
}
